import React from "react";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";

import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined"; // reminder gepland
import EditNotificationsOutlinedIcon from "@mui/icons-material/EditNotificationsOutlined"; // reminder ingevuld
import NotificationsIcon from "@mui/icons-material/Notifications"; // reminder nog niet ingevuld
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // ingevulde vragenlijst
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"; // vragenlijst overgeslaan
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"; // geplande vragenlijst
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"; // error
import Icon from "@mui/material/Icon";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";

import { dateFormat } from "../../tools";
import useClasses from "../../useClasses";
import { useTranslation } from "react-i18next";

const fontSize = 12;

const styles = (theme) => ({
  root: {
    width: "100%",
    height: "100%",
    border: 0,
    borderRadius: theme.shape.borderRadius,
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: 4,
  },
  timeText: {
    fontSize: fontSize,
  },
  titleText: {
    fontSize: fontSize,
    fontWeight: 500,
  },
});

export const getStatus = ({
  t,
  reminderForScheduledBeepId,
  sentTime,
  resultReceivedTime,
}) => {
  if (reminderForScheduledBeepId > 0 && sentTime && resultReceivedTime)
    return {
      title: t("scheduler.calendar.completed"),
      icon: EditNotificationsOutlinedIcon,
    }; // reminder verzonden en resultaat ontvangen
  if (reminderForScheduledBeepId > 0 && sentTime && !resultReceivedTime)
    return {
      title: t("scheduler.calendar.sent"),
      icon: NotificationsIcon,
    }; // reminder verzonden en geen resultaat ontvangen
  if (reminderForScheduledBeepId > 0 && !sentTime && !resultReceivedTime)
    return {
      title: t("scheduler.calendar.scheduled"),
      icon: NotificationsNoneOutlinedIcon,
    }; // reminder gepland
  if (sentTime && resultReceivedTime)
    return {
      title: t("scheduler.calendar.completed"),
      icon: CheckCircleIcon,
    }; // vragenlijst verzonden en resultaat ontvangen
  if (sentTime && !resultReceivedTime)
    return {
      title: t("scheduler.calendar.sent"),
      icon: FiberManualRecordIcon,
    }; // verzonden en geen resultaat ontvangen
  if (!sentTime && !resultReceivedTime)
    return {
      title: t("scheduler.calendar.scheduled"),
      icon: RadioButtonUncheckedIcon,
    }; // nog niet verzonden en geen resultaat ontvangen
  return { title: "", icon: HelpOutlineIcon };
};

function WeekEvent({ event }) {
  const classes = useClasses(styles);
  return (
    <Box display="flex">
      {event.extendedProps.templateId > -1 ? (
        <CollectionsBookmarkOutlinedIcon sx={{ mr: "4px" }} fontSize="small" />
      ) : null}
      <Typography noWrap className={classes.titleText}>
        {event.title}
      </Typography>
    </Box>
  );
}

function MonthEvent({ event }) {
  const classes = useClasses(styles);
  return (
    <React.Fragment>
      <Typography noWrap className={classes.titleText}>
        {event.title}
      </Typography>
    </React.Fragment>
  );
}

export default function Event({ event, timeText, view, id, locked }) {
  const classes = useClasses(styles);
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    sentTime,
    resultReceivedTime,
    reminderForScheduledBeepId,
    crossedOut = false,
  } = event.extendedProps;
  const isWeekEvent = view.type === "timeGridWeek";
  const iconColor = isWeekEvent ? "#fff" : event.backgroundColor;
  const textColor = event?.textColor ?? "#000";

  return (
    <div
      className={classes.root}
      style={{
        boxShadow: id === event.id ? theme.shadows[20] : "none",
        display:
          view.type === "dayGridMonth" ||
          (view.type === "timeGridWeek" &&
            moment(event.start, dateFormat).hours() === 23 &&
            moment(event.start, dateFormat).minutes() > 0)
            ? "flex"
            : "block",
        alignItems: "center",
        opacity: sentTime ? 0.7 : 1,
      }}
    >
      <div className={classes.titleContainer}>
        <Tooltip
          title={
            getStatus({
              t,
              sentTime,
              resultReceivedTime,
              reminderForScheduledBeepId,
            }).title
          }
          styles={{ zIndex: 9999 }}
        >
          {
            <Icon
              sx={{
                width: "18px",
                height: "18px",
                marginRight: "6px",
                color: iconColor,
              }}
              component={
                getStatus({
                  t,
                  sentTime,
                  resultReceivedTime,
                  reminderForScheduledBeepId,
                }).icon
              }
            />
          }
        </Tooltip>
        <Tooltip title={event.title} styles={{ zIndex: 9999 }}>
          <Typography
            className={classes.timeText}
            sx={{
              textDecoration: crossedOut ? "line-through" : "",
              color: textColor,
            }}
          >
            {moment(event.start, dateFormat).format("HH:mm")}
            {event.end !== null
              ? ` - ${moment(event.end, dateFormat).format("HH:mm")}`
              : null}
          </Typography>
        </Tooltip>
      </div>
      {view.type === "dayGridMonth" ? (
        <MonthEvent event={event} />
      ) : (
        <WeekEvent event={event} />
      )}
    </div>
  );
}
